import debounce from 'lodash/debounce'
import {defineStore} from 'pinia'
import {v4 as uuid} from 'uuid'
import {dashboard} from '@/utils/dashboard'

const debouncedSave = debounce((layouts, widgets) => {
    $lara.post(useNuxtApp().$apiRoute('spa.dashboard.store'), {body: {dashboard: {layouts, widgets}}})
}, 500)

type StoreDashboardWidget = {
    id: string,
    type?: string,
    title?: string,
    settings: Record<string, any>,
    x: number,
    y: number,
    w: number,
    h: number,
    maxW: number,
    maxH: number,
    minW: number,
    minH: number,
    isDraggable: boolean,
    isResizable: boolean,
    [key: string]: any
}

export const useLayoutStore = defineStore('layout', () => {

    const isSidebarOpen = ref(false)
    const wikiSidebarOpen = ref(false)
    const isSubheaderOpen = ref(false)
    const currentModule = ref<string | null>(null)
    const breakpoint = ref(null)
    const dashboardGrid = ref<StoreDashboardWidget[]>([]) // stores the local and visible state of the dashboard
    const dashboardWidgets = ref<StoreDashboardWidget[]>([]) // stores the actual widgets that are saved in the database important for updating on different devices
    const dashboardGridResponsive = ref<Record<string, StoreDashboardWidget[]>>({}) // stores the responsive state of the dashboard
    const loadingDashboard = ref(false)

    function setCurrentModule(newModule: string | null) {
        currentModule.value = newModule
    }

    async function loadDashboard() {
        if (dashboard.editable) {
            loadingDashboard.value = true
            const response = await $lara.get(useNuxtApp().$apiRoute('spa.dashboard.show'))
            loadingDashboard.value = false
            if (response.error) return
            dashboardGridResponsive.value = response?.layouts || {}
            dashboardWidgets.value = response?.widgets || []
            return
        }
        dashboardWidgets.value = dashboard.widgets.map((widget) => ({
            ...widget,
            id: uuid(),
            settings: {},
            dragAllowFrom: ".drag-area",
            x: widget.x || 0,
            y: widget.y || 0,
            w: widget.width || 1,
            h: widget.height || 1,
            maxW: widget.maxWidth || 4,
            maxH: widget.maxHeight || 4,
            minW: widget.minWidth || 1,
            minH: widget.minHeight || 1,
            isDraggable: false,
            isResizable: false
        }))

        dashboardGrid.value = dashboardWidgets.value
    }

    function saveDashboard() {
        if (dashboard.editable) {
            debouncedSave(dashboardGridResponsive.value, dashboardWidgets.value)
        }
    }

    function addWidget(widget: DashboardWidget) {
        const newWidget = {
            ...widget,
            id: uuid(),
            settings: {},
            dragAllowFrom: ".drag-area",
            x: 0,
            y: 0,
            w: widget.width || 1,
            h: widget.height || 1,
            maxW: widget.maxWidth || 4,
            maxH: widget.maxHeight || 4,
            minW: widget.minWidth || 1,
            minH: widget.minHeight || 1,
            isDraggable: (widget.draggable ?? true),
            isResizable: (widget.resizable ?? true)
        } as StoreDashboardWidget

        dashboardWidgets.value.push(newWidget)
    }

    function removeWidget(item: StoreDashboardWidget) {
        dashboardWidgets.value = dashboardWidgets.value.filter((widget) => widget.id !== item.id)
    }

    return {
        isSidebarOpen,
        wikiSidebarOpen,
        isSubheaderOpen,
        currentModule,
        breakpoint,
        dashboardGrid,
        dashboardWidgets,
        dashboardGridResponsive,
        loadingDashboard,
        setCurrentModule,
        loadDashboard,
        saveDashboard,
        addWidget,
        removeWidget
    }
})
